import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  factoryResetAionDevice,
  markAionDeviceLost,
  openAionDeviceSession,
  registerAionDevice,
  revokeAionDevice,
} from "@/services/api";

const DEVICE_CLASSES = ["AION_WALL", "AION_ROOM", "AION_MINI", "AION_HUB", "AION_VISION", "AION_REMOTE"];

const DEFAULT_CAPABILITIES = {
  AION_WALL: ["HOME", "TODAY", "MEMORY", "PROFILE_SWITCH"],
  AION_ROOM: ["VOICE", "SPEAKER", "ROOM_CONTEXT", "WAKE_WORD"],
  AION_MINI: ["VOICE", "SESSION", "ROOM_CONTEXT"],
  AION_HUB: ["LOCAL_CORE", "PROTOCOL_BRIDGE", "DEVICE_REGISTRY"],
  AION_VISION: ["VISION", "CAMERA_PRIVACY", "ROOM_CONTEXT"],
  AION_REMOTE: ["BUTTONS", "SCENE_TRIGGER", "DEVICE_TRIGGER"],
};

export const AionClientPlatformPanel = ({ devices = [], overview = null, rooms = [], sessions = [], token, onRefresh }) => {
  const [draft, setDraft] = useState({ deviceClass: "AION_WALL", model: "", roomId: "" });
  const [feedback, setFeedback] = useState("");
  const [pendingAction, setPendingAction] = useState("");

  useEffect(() => {
    if (!draft.roomId && rooms[0]?.id) {
      setDraft((prev) => ({ ...prev, roomId: rooms[0].id }));
    }
  }, [draft.roomId, rooms]);

  const roomMap = useMemo(() => Object.fromEntries(rooms.map((room) => [room.id, room.name])), [rooms]);
  const sessionCounts = useMemo(() => sessions.reduce((acc, session) => {
    acc[session.device_id] = (acc[session.device_id] || 0) + 1;
    return acc;
  }, {}), [sessions]);

  const runAction = async (key, callback, successMessage) => {
    setPendingAction(key);
    setFeedback("");
    try {
      await callback();
      setFeedback(successMessage);
      await onRefresh?.();
    } catch (error) {
      setFeedback(error.message || "AION client action failed.");
    } finally {
      setPendingAction("");
    }
  };

  const handleRegister = async () => runAction(
    "register",
    () => registerAionDevice({
      device_class: draft.deviceClass,
      model: draft.model || `${draft.deviceClass.replaceAll("_", " ")} Surface`,
      room_id: draft.roomId || undefined,
      capabilities: DEFAULT_CAPABILITIES[draft.deviceClass] || [],
    }, token),
    `${draft.deviceClass} enrolled successfully.`,
  );

  return (
    <section className="jarvis-panel" data-testid="aion-client-platform-panel">
      <div className="jarvis-panel-header">
        <div>
          <p className="jarvis-eyebrow" data-testid="aion-client-platform-eyebrow">AION CLIENT PLATFORM</p>
          <h2 className="jarvis-title" data-testid="aion-client-platform-title">Wall, Room, Mini, Hub, Vision & Remote</h2>
        </div>
        <div className="jarvis-chip" data-testid="aion-client-platform-chip">{overview?.device_count ?? devices.length} enrolled</div>
      </div>

      {feedback ? <div className="aion-note" data-testid="aion-client-platform-feedback">{feedback}</div> : null}

      <div className="aion-compact-status-grid" data-testid="aion-client-platform-overview-grid">
        <div className="aion-compact-status-box" data-testid="aion-client-overview-devices"><span>Clients</span><strong>{overview?.device_count ?? devices.length}</strong></div>
        <div className="aion-compact-status-box" data-testid="aion-client-overview-sessions"><span>Sessions</span><strong>{overview?.active_session_count ?? sessions.length}</strong></div>
        <div className="aion-compact-status-box" data-testid="aion-client-overview-trusted"><span>Trusted</span><strong>{overview?.trust_status?.TRUSTED ?? 0}</strong></div>
        <div className="aion-compact-status-box" data-testid="aion-client-overview-revoked"><span>Revoked</span><strong>{overview?.trust_status?.REVOKED ?? 0}</strong></div>
        <div className="aion-compact-status-box" data-testid="aion-client-overview-core-mode"><span>Core Mode</span><strong>{overview?.local_core?.mode || "HYBRID"}</strong></div>
        <div className="aion-compact-status-box" data-testid="aion-client-overview-network"><span>Network</span><strong>{overview?.local_core?.local_network_ok ? "LOCAL OK" : "DEGRADED"}</strong></div>
      </div>

      <div className="aion-settings-grid" data-testid="aion-client-enrollment-grid">
        <label className="aion-settings-field" data-testid="aion-client-class-field">
          <span>Device class</span>
          <Select value={draft.deviceClass} onValueChange={(value) => setDraft((prev) => ({ ...prev, deviceClass: value }))}>
            <SelectTrigger data-testid="aion-client-class-select"><SelectValue placeholder="Choose class" /></SelectTrigger>
            <SelectContent>
              {DEVICE_CLASSES.map((deviceClass) => <SelectItem key={deviceClass} value={deviceClass} data-testid={`aion-client-class-option-${deviceClass.toLowerCase()}`}>{deviceClass}</SelectItem>)}
            </SelectContent>
          </Select>
        </label>
        <label className="aion-settings-field" data-testid="aion-client-model-field">
          <span>Model label</span>
          <Input value={draft.model} onChange={(event) => setDraft((prev) => ({ ...prev, model: event.target.value }))} placeholder="Wall hallway / Mini kitchen" data-testid="aion-client-model-input" />
        </label>
        <label className="aion-settings-field" data-testid="aion-client-room-field">
          <span>Room</span>
          <Select value={draft.roomId} onValueChange={(value) => setDraft((prev) => ({ ...prev, roomId: value }))}>
            <SelectTrigger data-testid="aion-client-room-select"><SelectValue placeholder="Choose room" /></SelectTrigger>
            <SelectContent>
              {rooms.map((room) => <SelectItem key={room.id} value={room.id} data-testid={`aion-client-room-option-${room.id}`}>{room.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </label>
        <div className="aion-settings-field" data-testid="aion-client-submit-field">
          <span>Enrollment</span>
          <Button onClick={handleRegister} disabled={pendingAction === "register"} data-testid="aion-client-register-button">
            {pendingAction === "register" ? "Enrolling…" : "Enroll client"}
          </Button>
        </div>
      </div>

      <div className="aion-list" data-testid="aion-client-class-summary-list">
        {DEVICE_CLASSES.map((deviceClass) => (
          <div className="aion-list-row" key={deviceClass} data-testid={`aion-client-class-summary-${deviceClass.toLowerCase()}`}>
            <div>
              <strong>{deviceClass}</strong>
              <small>{overview?.client_classes?.[deviceClass] ?? 0} enrolled</small>
            </div>
          </div>
        ))}
      </div>

      <div className="aion-list" data-testid="aion-client-device-list">
        {devices.map((device) => {
          const deviceKey = device.device_id;
          const pendingKey = pendingAction.startsWith(deviceKey) ? pendingAction : "";
          return (
            <div className="aion-list-row" key={deviceKey} data-testid={`aion-client-device-${deviceKey}`}>
              <div>
                <strong data-testid={`aion-client-device-name-${deviceKey}`}>{device.model}</strong>
                <small data-testid={`aion-client-device-meta-${deviceKey}`}>{device.device_class} · {roomMap[device.room_id] || "Unassigned"} · {device.connection_mode} · {device.trust_status}</small>
                <small data-testid={`aion-client-device-session-count-${deviceKey}`}>{sessionCounts[deviceKey] || 0} sessions · OTA {device.ota_target}</small>
              </div>
              <div className="jarvis-device-actions" data-testid={`aion-client-device-actions-${deviceKey}`}>
                <Button size="sm" variant="outline" disabled={Boolean(pendingKey) || device.trust_status === "REVOKED"} onClick={() => runAction(`${deviceKey}:session`, () => openAionDeviceSession(deviceKey, {}, token), `Session opened for ${device.model}.`)} data-testid={`aion-client-open-session-${deviceKey}`}>Open session</Button>
                <Button size="sm" variant="outline" disabled={Boolean(pendingKey)} onClick={() => runAction(`${deviceKey}:lost`, () => markAionDeviceLost(deviceKey, token), `${device.model} marked as lost.`)} data-testid={`aion-client-mark-lost-${deviceKey}`}>Lost</Button>
                <Button size="sm" variant="outline" disabled={Boolean(pendingKey) || device.trust_status === "REVOKED"} onClick={() => runAction(`${deviceKey}:revoke`, () => revokeAionDevice(deviceKey, token), `${device.model} revoked.`)} data-testid={`aion-client-revoke-${deviceKey}`}>Revoke</Button>
                {device.trust_status === "REVOKED" ? <Button size="sm" disabled={Boolean(pendingKey)} onClick={() => runAction(`${deviceKey}:reset`, () => factoryResetAionDevice(deviceKey, token), `${device.model} factory reset.`)} data-testid={`aion-client-reset-${deviceKey}`}>Factory reset</Button> : null}
              </div>
            </div>
          );
        })}
      </div>

      <div className="aion-list" data-testid="aion-client-session-list">
        {sessions.slice(0, 6).map((session) => (
          <div className="aion-list-row" key={session.client_session_id} data-testid={`aion-client-session-${session.client_session_id}`}>
            <div>
              <strong>{session.device_id}</strong>
              <small>{session.device_class} · {session.connection_mode} · {session.conversation_owner ? "conversation owner" : "standby"}</small>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};